import React, { useEffect, useState, useRef } from "react"
import "./AddSchool.scss"
import SchoolApi from "Api/SchoolApi"
import * as Yup from "yup"
import { useNavigate } from "react-router-dom"
import { Button, Card, CardBody, Col, Row, Spinner, Input as ReactstrapInput, FormGroup, Label } from "reactstrap"
import CustomBreadCrumb from "utils/CustomBreadCrumb/CustomBreadCrumb"
import { InputText } from "primereact/inputtext"
import Country from "../../../utils/Data/Country"
import { Dropdown } from "primereact/dropdown"
import { InputTextarea } from "primereact/inputtextarea"
import { Toast } from "primereact/toast"
import { MultiSelect } from "primereact/multiselect"

const AddSchool = () => {
    document.title = "Add School"
    const [loading, setLoading] = useState(false)
    const toast = useRef(null)
    const navigate = useNavigate("")
    const accessToken = localStorage.getItem("accessToken")

    const schoolInitialData = {
        schoolName: "",
        displayName: "",
        type: [],
        mediumOfInstruction: "",
        yearEstablished: "",
        country: "",
        state: "",
        city: "",
        pinCode: "",
        address: "",
        email: "",
        contactNumber: "",
        officialWebsite: "",
        affiliationBoard: [],
        aboutContent: "",
        highlights: "",
        campusSize: "",
        noOfStudents: "",
        noOfTeachers: "",
        gradesOffered: "",
        noOfLabs: "",
        sportsFacilities: "",
        sportsAvailable: "",
        transportFacilities: "",
        hostelFacility: "",
        yearlyIntake: "",
    }

    const [schoolData, setSchoolData] = useState(schoolInitialData)
    const [errors, setErrors] = useState({})

    const handleSchoolDataChange = (name, value) => {
        setSchoolData({ ...schoolData, [name]: value })
        setErrors({ ...errors, [name]: "" })
    }

    const breadcrumbItems = [
        { label: "Pixwik", link: "/" },
        { label: "Schools", link: "/schoolList" },
        { label: "Add School", link: "/addSchool" },
    ]

    const handleBreadcrumbClick = link => {
        navigate(link)
    }

    const typeOptions = [
        { name: "Private", code: "Private" },
        { name: "Government", code: "Government" },
        { name: "International", code: "International" },
    ]

    const affiliationBoardOptions = [
        { name: "State Board", code: "State Board" },
        { name: "CBSE", code: "CBSE" },
        { name: "IB", code: "IB" },
        { name: "CAIE", code: "CAIE" },
        { name: "IGCSE", code: "IGCSE" },
        { name: "ICSE", code: "ICSE" },
    ]

    const yesNoOptions = [
        { name: "Yes", code: "Yes" },
        { name: "No", code: "No" },
    ]

    const gradesOptions = [
        { name: "Pre-KG", code: "Pre-KG" },
        { name: "LKG", code: "LKG" },
        { name: "UKG", code: "UKG" },
        { name: "1st", code: "1st" },
        { name: "2nd", code: "2nd" },
        { name: "3rd", code: "3rd" },
        { name: "4th", code: "4th" },
        { name: "5th", code: "5th" },
        { name: "6th", code: "6th" },
        { name: "7th", code: "7th" },
        { name: "8th", code: "8th" },
        { name: "9th", code: "9th" },
        { name: "10th", code: "10th" },
        { name: "11th", code: "11th" },
        { name: "12th", code: "12th" },
    ]

    const ValidateSchool = Yup.object().shape({
        schoolName: Yup.string().required("School name is required"),
        displayName: Yup.string().required("Display name is required"),
        type: Yup.array().min(1, "At least one type is required"),
        country: Yup.string().required("Country is required"),
        state: Yup.string().required("State is required"),
        city: Yup.string().required("City is required"),
        pinCode: Yup.string().required("Pin code is required"),
        address: Yup.string().required("Address is required"),
        highlights: Yup.string().required("Highlights is required"),
        aboutContent: Yup.string().required("About content is required"),
        affiliationBoard: Yup.array().min(1, "At least one affiliation board is required"),
    })

    const addSchool = async () => {
        try {
            setLoading(true)

            await ValidateSchool.validate(
                {
                    ...schoolData,
                    country: schoolData.country?.name || schoolData.country,
                },
                { abortEarly: false }
            )

            const payload = {
                ...schoolData,
                country: schoolData?.country?.name,
                type: schoolData?.type?.map(t => t.code || t),
                affiliationBoard: schoolData.affiliationBoard.map(board =>
                    board.code || board
                ),
                sportsAvailable: schoolData.sportsAvailable?.code === "Yes",
                transportFacilities: schoolData.transportFacilities?.code === "Yes",
                hostelFacility: schoolData.hostelFacility?.code === "Yes",
            }

            const config = {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            }

            const response = await SchoolApi.addSchool(payload, config)

            if (response.data.status) {
                toast.current.show({
                    severity: "success",
                    summary: "Success",
                    detail: response.data.message,
                    life: 3000,
                })
                setTimeout(() => {
                    navigate("/schoolList")
                }, 1000)
            } else {
                toast.current.show({
                    severity: "error",
                    summary: "Error",
                    detail: response.data.message,
                    life: 3000,
                })
            }
        } catch (error) {
            if (error.name === "ValidationError") {
                const validationErrors = {}
                error.inner.forEach(err => {
                    validationErrors[err.path] = err.message
                })
                setErrors(validationErrors)
                toast.current.show({
                    severity: "error",
                    summary: "Validation Error",
                    detail: "Please fill all required fields",
                    life: 3000,
                })
            } else {
                console.error("Error in addSchool:", error)
                toast.current.show({
                    severity: "error",
                    summary: "Error",
                    detail: error.response?.data?.message || "An unexpected error occurred",
                    life: 3000,
                })
            }
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="add-school">
            <Toast ref={toast} />
            <Row>
                <Col xs="12">
                    <div className="school-list-header d-flex  align-items-center justify-content-between">
                        <CustomBreadCrumb
                            data={breadcrumbItems}
                            pageTitle="Add School"
                            handleBreadcrumbClick={handleBreadcrumbClick}
                        />
                    </div>

                    <Card className="mt-4 ">
                        <CardBody>
                            <div className="school-add-form ">
                                <div className="school-add-inp">
                                    <label htmlFor="schoolName" className="label">
                                        School Name <small className="text-danger">*</small>
                                    </label>
                                    <div className="inp-err">
                                        <InputText
                                            id="schoolName"
                                            value={schoolData.schoolName}
                                            onChange={e =>
                                                handleSchoolDataChange("schoolName", e.target.value)
                                            }
                                            placeholder="Enter School Name"
                                            className="inp-field-box"
                                        />
                                        {errors.schoolName && (
                                            <small className="error">{errors.schoolName}</small>
                                        )}
                                    </div>
                                </div>

                                <div className="school-add-inp">
                                    <label htmlFor="displayName" className="label">
                                        Display Name <small className="text-danger">*</small>
                                    </label>
                                    <div className="inp-err">
                                        <InputText
                                            id="displayName"
                                            value={schoolData.displayName}
                                            onChange={e =>
                                                handleSchoolDataChange("displayName", e.target.value)
                                            }
                                            placeholder="Enter Display Name"
                                            className="inp-field-box"
                                        />
                                        {errors.displayName && (
                                            <small className="error">{errors.displayName}</small>
                                        )}
                                    </div>
                                </div>

                                <div className="school-add-inp">
                                    <label htmlFor="type" className="label">
                                        Type <small className="text-danger">*</small>
                                    </label>
                                    <div className="inp-err">
                                        <MultiSelect
                                            value={schoolData.type}
                                            onChange={e => handleSchoolDataChange("type", e.value)}
                                            options={typeOptions}
                                            optionLabel="name"
                                            placeholder="Select Type"
                                            display="chip"
                                            className="inp-field-box"
                                        />
                                        {errors.type && (
                                            <small className="error">{errors.type}</small>
                                        )}
                                    </div>
                                </div>

                                <div className="school-add-inp">
                                    <label htmlFor="mediumOfInstruction" className="label">Medium of Instruction</label>
                                    <div className="inp-err">
                                        <InputText
                                            id="mediumOfInstruction"
                                            value={schoolData.mediumOfInstruction}
                                            onChange={e =>
                                                handleSchoolDataChange("mediumOfInstruction", e.target.value)
                                            }
                                            placeholder="Enter Medium of Instruction"
                                            className="inp-field-box"
                                        />
                                    </div>
                                </div>

                                <div className="school-add-inp">
                                    <label htmlFor="yearEstablished" className="label">Year Established</label>
                                    <div className="inp-err">
                                        <InputText
                                            id="yearEstablished"
                                            value={schoolData.yearEstablished}
                                            onChange={e =>
                                                handleSchoolDataChange("yearEstablished", e.target.value)
                                            }
                                            placeholder="Enter Year"
                                            className="inp-field-box"
                                        />
                                    </div>
                                </div>

                                <div className="school-add-inp">
                                    <label htmlFor="country" className="label">
                                        Country <small className="text-danger">*</small>
                                    </label>
                                    <div className="inp-err">
                                        <Dropdown
                                            value={schoolData.country}
                                            onChange={e => handleSchoolDataChange("country", e.value)}
                                            options={Country}
                                            optionLabel="name"
                                            placeholder="Select Country"
                                            filter
                                            className="inp-field-box"
                                        />
                                        {errors.country && (
                                            <small className="error">{errors.country}</small>
                                        )}
                                    </div>
                                </div>

                                <div className="school-add-inp">
                                    <label htmlFor="state" className="label">
                                        State <small className="text-danger">*</small>
                                    </label>
                                    <div className="inp-err">
                                        <InputText
                                            id="state"
                                            value={schoolData.state}
                                            onChange={e =>
                                                handleSchoolDataChange("state", e.target.value)
                                            }
                                            placeholder="Enter State"
                                            className="inp-field-box"
                                        />
                                        {errors.state && (
                                            <small className="error">{errors.state}</small>
                                        )}
                                    </div>
                                </div>

                                <div className="school-add-inp">
                                    <label htmlFor="city" className="label">
                                        City <small className="text-danger">*</small>
                                    </label>
                                    <div className="inp-err">
                                        <InputText
                                            id="city"
                                            value={schoolData.city}
                                            onChange={e =>
                                                handleSchoolDataChange("city", e.target.value)
                                            }
                                            placeholder="Enter City"
                                            className="inp-field-box"
                                        />
                                        {errors.city && (
                                            <small className="error">{errors.city}</small>
                                        )}
                                    </div>
                                </div>

                                <div className="school-add-inp">
                                    <label htmlFor="pinCode" className="label">
                                        Pin Code <small className="text-danger">*</small>
                                    </label>
                                    <div className="inp-err">
                                        <InputText
                                            id="pinCode"
                                            value={schoolData.pinCode}
                                            onChange={e =>
                                                handleSchoolDataChange("pinCode", e.target.value)
                                            }
                                            placeholder="Enter Pin Code"
                                            className="inp-field-box"
                                        />
                                        {errors.pinCode && (
                                            <small className="error">{errors.pinCode}</small>
                                        )}
                                    </div>
                                </div>

                                <div className="school-add-inp">
                                    <label htmlFor="address" className="label">
                                        Address <small className="text-danger">*</small>
                                    </label>
                                    <div className="inp-err">
                                        <InputTextarea
                                            id="address"
                                            value={schoolData.address}
                                            onChange={e =>
                                                handleSchoolDataChange("address", e.target.value)
                                            }
                                            placeholder="Enter Address"
                                            rows={3}
                                            className="inp-field-box"
                                        />
                                        {errors.address && (
                                            <small className="error">{errors.address}</small>
                                        )}
                                    </div>
                                </div>

                                <div className="school-add-inp">
                                    <label htmlFor="email" className="label">Email</label>
                                    <div className="inp-err">
                                        <InputText
                                            id="email"
                                            type="email"
                                            value={schoolData.email}
                                            onChange={e =>
                                                handleSchoolDataChange("email", e.target.value)
                                            }
                                            placeholder="Enter Email"
                                            className="inp-field-box"
                                        />
                                    </div>
                                </div>

                                <div className="school-add-inp">
                                    <label htmlFor="contactNumber" className="label">Contact Number</label>
                                    <div className="inp-err">
                                        <InputText
                                            id="contactNumber"
                                            value={schoolData.contactNumber}
                                            onChange={e =>
                                                handleSchoolDataChange("contactNumber", e.target.value)
                                            }
                                            placeholder="Enter Contact Number"
                                            className="inp-field-box"
                                        />
                                    </div>
                                </div>

                                <div className="school-add-inp">
                                    <label htmlFor="officialWebsite" className="label">Official Website</label>
                                    <div className="inp-err">
                                        <InputText
                                            id="officialWebsite"
                                            value={schoolData.officialWebsite}
                                            onChange={e =>
                                                handleSchoolDataChange("officialWebsite", e.target.value)
                                            }
                                            placeholder="Enter Website URL"
                                            className="inp-field-box"
                                        />
                                    </div>
                                </div>

                                <div className="school-add-inp">
                                    <label htmlFor="affiliationBoard" className="label">
                                        Affiliation Board <small className="text-danger">*</small>
                                    </label>
                                    <div className="inp-err">
                                        <MultiSelect
                                            value={schoolData.affiliationBoard}
                                            onChange={e =>
                                                handleSchoolDataChange("affiliationBoard", e.value)
                                            }
                                            options={affiliationBoardOptions}
                                            optionLabel="name"
                                            placeholder="Select Affiliation Boards"
                                            display="chip"
                                            className="inp-field-box"
                                        />
                                        {errors.affiliationBoard && (
                                            <small className="error">{errors.affiliationBoard}</small>
                                        )}
                                    </div>
                                </div>

                                <div className="school-add-inp">
                                    <label htmlFor="aboutContent" className="label">
                                        About Content <small className="text-danger">*</small>
                                    </label>
                                    <div className="inp-err">
                                        <InputTextarea
                                            id="aboutContent"
                                            value={schoolData.aboutContent}
                                            onChange={e =>
                                                handleSchoolDataChange("aboutContent", e.target.value)
                                            }
                                            placeholder="Enter About Content"
                                            rows={5}
                                            className="inp-field-box"
                                        />
                                        {errors.aboutContent && (
                                            <small className="error">{errors.aboutContent}</small>
                                        )}
                                    </div>
                                </div>

                                <div className="school-add-inp">
                                    <label htmlFor="highlights" className="label">
                                        Highlights <small className="text-danger">*</small>
                                    </label>
                                    <div className="inp-err">
                                        <InputTextarea
                                            id="highlights"
                                            value={schoolData.highlights}
                                            onChange={e =>
                                                handleSchoolDataChange("highlights", e.target.value)
                                            }
                                            placeholder="Enter Highlights"
                                            rows={4}
                                            className="inp-field-box"
                                        />
                                        {errors.highlights && (
                                            <small className="error">{errors.highlights}</small>
                                        )}
                                    </div>
                                </div>

                                <div className="school-add-inp">
                                    <label htmlFor="campusSize" className="label">Campus Size</label>
                                    <div className="inp-err">
                                        <InputText
                                            id="campusSize"
                                            value={schoolData.campusSize}
                                            onChange={e =>
                                                handleSchoolDataChange("campusSize", e.target.value)
                                            }
                                            placeholder="Enter Campus Size"
                                            className="inp-field-box"
                                        />
                                    </div>
                                </div>

                                <div className="school-add-inp">
                                    <label htmlFor="noOfStudents" className="label">Number of Students</label>
                                    <div className="inp-err">
                                        <InputText
                                            id="noOfStudents"
                                            value={schoolData.noOfStudents}
                                            onChange={e =>
                                                handleSchoolDataChange("noOfStudents", e.target.value)
                                            }
                                            placeholder="Enter Number of Students"
                                            className="inp-field-box"
                                        />
                                    </div>
                                </div>

                                <div className="school-add-inp">
                                    <label htmlFor="noOfTeachers" className="label">Number of Teachers</label>
                                    <div className="inp-err">
                                        <InputText
                                            id="noOfTeachers"
                                            value={schoolData.noOfTeachers}
                                            onChange={e =>
                                                handleSchoolDataChange("noOfTeachers", e.target.value)
                                            }
                                            placeholder="Enter Number of Teachers"
                                            className="inp-field-box"
                                        />
                                    </div>
                                </div>

                                <div className="school-add-inp">
                                    <label htmlFor="gradesOffered" className="label">Grades Offered</label>
                                    <div className="inp-err">
                                        <InputText
                                            id="gradesOffered"
                                            value={schoolData.gradesOffered}
                                            onChange={e =>
                                                handleSchoolDataChange("gradesOffered", e.target.value)
                                            }
                                            placeholder="Enter Grades Offered (e.g., Pre-KG to 12th)"
                                            className="inp-field-box"
                                        />
                                    </div>
                                </div>

                                <div className="school-add-inp">
                                    <label htmlFor="noOfLabs" className="label">Number of Labs</label>
                                    <div className="inp-err">
                                        <InputText
                                            id="noOfLabs"
                                            value={schoolData.noOfLabs}
                                            onChange={e =>
                                                handleSchoolDataChange("noOfLabs", e.target.value)
                                            }
                                            placeholder="Enter Number of Labs"
                                            className="inp-field-box"
                                        />
                                    </div>
                                </div>

                                <div className="school-add-inp">
                                    <label htmlFor="sportsAvailable" className="label">Sports Available</label>
                                    <div className="inp-err">
                                        <Dropdown
                                            id="sportsAvailable"
                                            value={schoolData.sportsAvailable}
                                            onChange={e => handleSchoolDataChange("sportsAvailable", e.value)}
                                            options={yesNoOptions}
                                            optionLabel="name"
                                            placeholder="Select Sports Available"
                                            className="inp-field-box"
                                        />
                                    </div>
                                </div>

                                {schoolData.sportsAvailable?.code === "Yes" && (
                                    <div className="school-add-inp">
                                        <label htmlFor="sportsFacilities" className="label">Sports Facilities</label>
                                        <div className="inp-err">
                                            <InputText
                                                id="sportsFacilities"
                                                value={schoolData.sportsFacilities}
                                                onChange={e =>
                                                    handleSchoolDataChange("sportsFacilities", e.target.value)
                                                }
                                                placeholder="Enter Sports Facilities (e.g., Cricket, Football, Basketball)"
                                                className="inp-field-box"
                                            />
                                        </div>
                                    </div>
                                )}

                                <div className="school-add-inp">
                                    <label htmlFor="transportFacilities" className="label">Transport Facilities</label>
                                    <div className="inp-err">
                                        <Dropdown
                                            id="transportFacilities"
                                            value={schoolData.transportFacilities}
                                            onChange={e => handleSchoolDataChange("transportFacilities", e.value)}
                                            options={yesNoOptions}
                                            optionLabel="name"
                                            placeholder="Select Transport Facilities"
                                            className="inp-field-box"
                                        />
                                    </div>
                                </div>

                                <div className="school-add-inp">
                                    <label htmlFor="hostelFacility" className="label">Hostel Facility</label>
                                    <div className="inp-err">
                                        <Dropdown
                                            id="hostelFacility"
                                            value={schoolData.hostelFacility}
                                            onChange={e => handleSchoolDataChange("hostelFacility", e.value)}
                                            options={yesNoOptions}
                                            optionLabel="name"
                                            placeholder="Select Hostel Facility"
                                            className="inp-field-box"
                                        />
                                    </div>
                                </div>

                                <div className="school-add-inp">
                                    <label htmlFor="yearlyIntake" className="label">Yearly Intake</label>
                                    <div className="inp-err">
                                        <InputText
                                            id="yearlyIntake"
                                            value={schoolData.yearlyIntake}
                                            onChange={e =>
                                                handleSchoolDataChange("yearlyIntake", e.target.value)
                                            }
                                            placeholder="Enter Yearly Intake"
                                            className="inp-field-box"
                                        />
                                    </div>
                                </div>

                            </div>
                            <div className="save-cancel-btn mt-4 d-flex align-items-center justify-content-end gap-3">
                                <Button
                                    type="button"
                                    outline
                                    color="danger"
                                    className="waves-effect waves-light"
                                    onClick={() => {
                                        navigate(-1)
                                    }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="button"
                                    color="secondary"
                                    className="waves-effect waves-light"
                                    disabled={loading}
                                    onClick={() => {
                                        addSchool()
                                    }}
                                >
                                    {loading ? <Spinner size="sm" /> : "Add"}
                                </Button>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </div>
    )
}

export default AddSchool
